import React from 'react'
import { head, isEmpty } from 'ramda'
import { ButtonContainer } from 'sidecar-components/atoms/ButtonContainer'
import { IconButton } from 'sidecar-components/molecules/IconButton'
import { IconSize } from 'sidecar-components/atoms/Icon'
import { Modal } from 'sidecar-components/molecules/Modal'
import { ButtonSentiment } from 'sidecar-components/atoms/Button'

import { getLabsAndImagingText } from './utils'
import { LearnMoreButton } from './LearnMoreButton'
import { Provider } from './PreCheckedProviders.types'

interface HeaderModalProps {
  /** Description of the service category */
  description?: string
  /** Whether the sidecar code type is Facility */
  isFacility: boolean
  /** Whether the sidecar code type is Professional */
  isProfessional: boolean
  /** Array of professional providers */
  professionalProviders?: Provider[]
  /** Array of all providers */
  providers?: Provider[]
  /** Type of sidecar code (Professional or Facility) */
  sidecarCodeType?: string
  /** Type of component usage context */
  type?: string
  /** Whether the component is used in Premember */
  premember?: boolean
}

export const HeaderModal: React.FC<HeaderModalProps> = ({
  description = '',
  isFacility,
  isProfessional,
  professionalProviders = [],
  providers = [],
  sidecarCodeType = '',
  type = '',
  premember = false,
}) => {
  // ----------- Header & Tool Tip Logic -----------
  // a primary facility will exist when the sidecarCodeType is Facility and there is a facility provider
  // under the condition where the primary facility condition is met,
  // there will be 1 facility and 1 professional provider that are not the same,
  // display specific text in the upper portion of the modal
  const pluralText = professionalProviders?.length > 1 ? 'these' : 'this'
  const pluralProfessionalProviderText =
    professionalProviders?.length > 1 ? 'providers' : 'provider'
  const sidecarCodeText = isProfessional ? 'provider' : 'facility'
  const testId = `precheckedHeaderTooltip_${sidecarCodeText.toLowerCase()}`
  const primaryFacility =
    isFacility &&
    head(
      providers.filter(provider => provider.categoryType === sidecarCodeType),
    )
  const otherServicesText = getLabsAndImagingText(providers)

  // Only show displaySchedulingText if:
  // 1) primary provider is "Facility"
  // 2) professional and facility providers are not the same
  const displaySchedulingText =
    isFacility &&
    !isEmpty(professionalProviders) &&
    primaryFacility?.providerName !== professionalProviders[0]?.providerName

  // Care detail and EOC specific paragraph if the displaySchedulingText conditions are met
  const careDetailsParagraph = /(careDetail|episodeOfCare)/.test(type) &&
    !!displaySchedulingText && (
      <>
        <div data-testid="info_before_scheduling">
          <h4>Before scheduling</h4>
          <p className="sh-body-sm">
            {professionalProviders[0]?.providerName} is the pre-checked provider
            affiliated with {primaryFacility?.providerName}. If you need to
            schedule a surgery or procedure, ask your{' '}
            {professionalProviders[0]?.providerName}
            doctor to schedule it at {primaryFacility?.providerName}.
          </p>
          <h4>More about pre-checked facilities</h4>
        </div>
      </>
    )

  // ----------- Header modal -----------
  return (
    <>
      <Modal
        size="lg"
        title="Pre-checked providers"
        trigger={
          <IconButton
            className="shrink-0"
            icon="tooltip"
            size={IconSize.SM}
            testId={`btn_${testId}`}
            label="Learn more"
          />
        }
      >
        {/* care detail and EOC specific paragraph */}
        {(type === 'careDetail' || type === 'episodeOfCare') &&
          careDetailsParagraph}

        {/* general content for all types */}
        <p className="sh-body-sm">
          If your deductible has been met or it does not apply:
        </p>
        <ul className="sh-body-sm sh-ul-sm">
          <li>
            You won't pay anything for {description} services at {pluralText}{' '}
            pre-checked {pluralProfessionalProviderText}.
          </li>
        </ul>
        <p className="sh-body-sm">
          If your deductible has not been met and it applies:
        </p>
        <ul className="sh-body-sm sh-ul-sm">
          <li>
            <span className="capitalize">{pluralText}</span> pre-checked
            provider's price for {description} services is usually at or below
            the average cash price, in which case the Benefit Amount will apply
            to your deductible, and you will be responsible to the pre-checked
            provider for its charge.
          </li>
          <li>
            If the pre-checked provider charges more than the average cash
            price, then the provider's charge will apply to the deductible. In
            such case, you will be responsible for the entire amount of the
            pre-checked provider's charge, which may exceed the Benefit Amount
            shown in the member portal.
          </li>
        </ul>

        <h4>Other important notes on pre-checked providers:</h4>
        <ul className="sh-body-sm sh-ul-sm">
          <li>
            Itemized invoices for this service must include the name of the
            pre-checked provider.
          </li>
          <li>
            To find pre-checked providers for other services, including{' '}
            {otherServicesText}, conduct a new search.
          </li>
          <li>Ask for discounted rates.</li>
          <li>
            For services costing more than $2,500, contact Member Care to
            increase swipe limits.
          </li>
          <li>
            Because provider prices can change, providers identified as
            pre-checked are also subject to change.
          </li>
        </ul>

        {/* the button container and padding are needed for the active 
        button states to display correctly */}
        {!premember && (
          <ButtonContainer className="pb-2">
            <LearnMoreButton
              icon="forward"
              sentiment={ButtonSentiment.SECONDARY}
              testId={testId}
            />
          </ButtonContainer>
        )}
      </Modal>
    </>
  )
}
